import { useCallback, useEffect, useState } from 'react';
import { fetchTierConfig, resetTierConfig, saveTierConfig } from '../api';
import {
  GENERATION_TIERS,
  type ApiEndpointModel,
  type GenerationTier,
  type ModelTierConfig,
  type TierOverride,
} from '../types';

type TierValues = Record<GenerationTier, Record<string, string>>;

const TIER_LABELS: Record<GenerationTier, string> = {
  tier0: 'Tier 0',
  tier1: 'Tier 1',
  tier2: 'Tier 2',
  tier3: 'Tier 3',
  tier4: 'Tier 4',
};

const emptyValues = (): TierValues =>
  Object.fromEntries(GENERATION_TIERS.map((t) => [t, {}])) as TierValues;

/** Flatten the server's setups into editable strings (absent field = empty box). */
function toValues(config: ModelTierConfig): TierValues {
  const next = emptyValues();
  for (const tier of GENERATION_TIERS) {
    const override = config.tiers?.[tier] || {};
    for (const field of config.tier_fields) {
      const value = override[field.name];
      next[tier][field.name] = value == null ? '' : String(value);
    }
  }
  return next;
}

/** Whether a tier changes anything at all — drives the "customised" dot. */
function hasValues(values: Record<string, string> | undefined): boolean {
  return Object.values(values || {}).some((v) => v.trim() !== '');
}

interface TierSettingsCardProps {
  model: ApiEndpointModel;
}

/**
 * Editor for an endpoint's four generation-tier setups. Unlike the pricing card
 * below it, this writes to the server and changes how calls are actually run.
 */
export function TierSettingsCard({ model }: TierSettingsCardProps) {
  const [config, setConfig] = useState<ModelTierConfig | null>(null);
  const [values, setValues] = useState<TierValues>(emptyValues);
  const [activeTier, setActiveTier] = useState<GenerationTier>('tier4');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  const apply = useCallback((next: ModelTierConfig) => {
    setConfig(next);
    setValues(toValues(next));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchTierConfig(model.key)
      .then((next) => {
        if (cancelled) return;
        apply(next);
        setActiveTier(next.default_tier || 'tier4');
      })
      .catch((ex) => {
        if (!cancelled) setError(ex instanceof Error ? ex.message : String(ex));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [model.key, apply]);

  function handleChange(tier: GenerationTier, field: string, value: string) {
    setValues((prev) => ({ ...prev, [tier]: { ...prev[tier], [field]: value } }));
  }

  async function run(action: () => Promise<ModelTierConfig>) {
    setSaving(true);
    setError(null);
    try {
      apply(await action());
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSaving(false);
    }
  }

  function handleSave() {
    // Blank boxes are sent as-is; the server drops them, which is what makes
    // clearing a field mean "fall back to the model default".
    const payload: Record<string, TierOverride> = {};
    for (const tier of GENERATION_TIERS) {
      const override: TierOverride = {};
      for (const [name, value] of Object.entries(values[tier] || {})) {
        if (value.trim() !== '') override[name] = value.trim();
      }
      payload[tier] = override;
    }
    void run(() => saveTierConfig(model.key, payload));
  }

  function handleReset() {
    void run(() => resetTierConfig(model.key));
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-soft p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-1">Generation tiers</h2>
        <p className="text-sm text-slate-400">Loading tier setups…</p>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-soft p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-1">Generation tiers</h2>
        <p className="text-sm text-rose-600">{error || 'Tier setups are unavailable.'}</p>
      </div>
    );
  }

  const fields = config.tier_fields;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-soft p-5">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h2 className="text-sm font-semibold text-slate-700">Generation tiers</h2>
        {config.overridden && (
          <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
            Custom setup active
          </span>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-4">
        A request carries the tier its merch run resolved, and{' '}
        <span className="font-medium text-slate-700">{model.label}</span> serves it with the setup
        below. Leave a field blank to use the model default. These settings are saved on the server
        and <em>do</em> change how calls are generated.
      </p>

      <div className="flex flex-wrap gap-1 mb-4 border-b border-slate-200">
        {GENERATION_TIERS.map((tier) => (
          <button
            key={tier}
            type="button"
            onClick={() => setActiveTier(tier)}
            className={`relative px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTier === tier
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {TIER_LABELS[tier]}
            {tier === config.default_tier && (
              <span className="ml-1 text-[10px] text-slate-400">default</span>
            )}
            {hasValues(values[tier]) && (
              <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-indigo-500 align-middle" />
            )}
          </button>
        ))}
      </div>

      {fields.length === 0 ? (
        <p className="text-sm text-slate-400">This endpoint has no tunable tier fields.</p>
      ) : (
        <div className="space-y-4">
          {fields.map((field) => {
            // The model default doubles as the placeholder, so an empty box
            // reads as "this is what you'll get". Some fields have no default
            // at all, in which case the provider's own fallback applies.
            const fallback = config.defaults[field.name];
            const placeholder = fallback == null ? 'model default' : String(fallback);
            const value = values[activeTier]?.[field.name] ?? '';
            return (
              <label key={field.name} className="block">
                <span className="text-xs font-medium text-slate-500">{field.label}</span>
                {field.type === 'select' ? (
                  <select
                    value={value}
                    onChange={(e) => handleChange(activeTier, field.name, e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
                  >
                    <option value="">
                      {fallback == null ? 'Model default' : `Model default (${fallback})`}
                    </option>
                    {(field.options || []).map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.type === 'number' ? 'number' : 'text'}
                    min={field.type === 'number' ? '0' : undefined}
                    value={value}
                    placeholder={placeholder}
                    onChange={(e) => handleChange(activeTier, field.name, e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
                  />
                )}
                <span className="mt-1 block text-[11px] text-slate-400">{field.help}</span>
              </label>
            );
          })}

          {error && <p className="text-xs text-rose-600">{error}</p>}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-500 text-white text-sm font-medium hover:bg-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {savedFlash ? 'Saved' : 'Save tiers'}
            </button>
            <button
              type="button"
              onClick={handleReset}
              disabled={saving || !config.overridden}
              className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Reset to defaults
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
